import { store } from '@/app/store';
import { env } from '@/config/env';
import { normalizeApiPayload } from '@/utils/normalizeApi';

type ClientErrorPayload = {
  source: string;
  category: string;
  message: string;
  fingerprint?: string;
  correlationId?: string;
  statusCode?: number;
  httpMethod?: string;
  route?: string;
  pageUrl?: string;
  stackTrace?: string;
  requestContext?: unknown;
  responseContext?: unknown;
  appVersion?: string;
};

const recentFingerprints = new Map<string, number>();

function buildUrl(path: string) {
  if (env.VITE_API_BASE_URL.startsWith('/')) {
    return `${env.VITE_API_BASE_URL}${path}`;
  }

  return new URL(path, env.VITE_API_BASE_URL).toString();
}

export function createCorrelationId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `corr-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function buildFingerprint(payload: ClientErrorPayload) {
  return [
    payload.source,
    payload.category,
    payload.statusCode ?? 'na',
    payload.httpMethod ?? 'na',
    payload.route ?? 'na',
    payload.message
  ].join('|');
}

function shouldSkipDuplicate(fingerprint: string) {
  const now = Date.now();
  const previous = recentFingerprints.get(fingerprint);
  recentFingerprints.set(fingerprint, now);

  for (const [key, timestamp] of recentFingerprints.entries()) {
    if (now - timestamp > 5000) {
      recentFingerprints.delete(key);
    }
  }

  return typeof previous === 'number' && now - previous < 5000;
}

export async function reportClientError(payload: ClientErrorPayload) {
  const state = store.getState();
  if (!state.auth.isAuthenticated || !state.auth.accessToken) {
    return;
  }

  const normalizedPayload: ClientErrorPayload = {
    ...payload,
    category: payload.category || 'runtime',
    source: payload.source || 'frontend-runtime',
    route: payload.route ?? window.location.pathname,
    pageUrl: payload.pageUrl ?? window.location.href,
    appVersion: payload.appVersion ?? env.VITE_APP_NAME
  };

  const fingerprint = normalizedPayload.fingerprint ?? buildFingerprint(normalizedPayload);
  if (shouldSkipDuplicate(fingerprint)) {
    return;
  }

  normalizedPayload.fingerprint = fingerprint;

  try {
    await fetch(buildUrl('/telemetry/errors'), {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${state.auth.accessToken}`,
        'x-clinic-id': state.auth.clinicId ?? 'clinic-main',
        'x-user-id': state.auth.userName ?? 'web-user',
        'x-correlation-id': normalizedPayload.correlationId ?? createCorrelationId(),
        ...(env.VITE_FUNCTIONS_KEY ? { 'x-functions-key': env.VITE_FUNCTIONS_KEY } : {})
      },
      body: JSON.stringify(normalizeApiPayload(normalizedPayload))
    });
  } catch {
    // telemetry failures are intentionally ignored
  }
}

export function startGlobalTelemetry() {
  const onError = (event: ErrorEvent) => {
    void reportClientError({
      source: 'frontend-runtime',
      category: 'window-error',
      message: event.message || 'Unhandled browser error',
      stackTrace: event.error instanceof Error ? event.error.stack : undefined
    });
  };

  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
    void reportClientError({
      source: 'frontend-runtime',
      category: 'unhandled-rejection',
      message: reason.message,
      stackTrace: reason.stack
    });
  };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);

  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onUnhandledRejection);
  };
}
